import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import {
  COMPLIANCE_LABELS,
  formatReceiptAmount,
  isReceiptType,
  type ComplianceRecord,
  type ComplianceType,
} from '../../types/compliance'

interface ComplianceHistoryModalProps {
  open: boolean
  employeeId: number
  type: ComplianceType
  onClose: () => void
}

export function ComplianceHistoryModal({
  open,
  employeeId,
  type,
  onClose,
}: ComplianceHistoryModalProps) {
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState<ComplianceRecord[]>([])
  const isReceipt = isReceiptType(type)

  const loadHistory = useCallback(async () => {
    if (!employeeId) return
    setLoading(true)
    try {
      const response = await fetchAPI(`/api/hr/employees/${employeeId}/compliance/${type}/history`)
      setRecords(response?.data ?? [])
    } catch {
      setRecords([])
    } finally {
      setLoading(false)
    }
  }, [employeeId, type])

  useEffect(() => {
    if (open) {
      loadHistory()
    }
  }, [open, loadHistory])

  const columns: ColumnsType<ComplianceRecord> = useMemo(() => {
    if (isReceipt) {
      return [
        {
          title: 'Сумма',
          dataIndex: 'amount',
          key: 'amount',
          render: val => formatReceiptAmount(val != null ? String(val) : null),
        },
        {
          title: 'Добавлено',
          dataIndex: 'created_at',
          key: 'created_at',
          render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : '—'),
        },
      ]
    }

    return [
      {
        title: 'Дата окончания',
        dataIndex: 'expires_at',
        key: 'expires_at',
        render: val => (val ? dayjs(val).format('DD.MM.YYYY') : '—'),
      },
      {
        title: 'Добавлено',
        dataIndex: 'created_at',
        key: 'created_at',
        render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : '—'),
      },
    ]
  }, [isReceipt])

  return (
    <Modal
      title={`История: ${COMPLIANCE_LABELS[type]}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={records}
        columns={columns}
        pagination={false}
        size="small"
        locale={{ emptyText: isReceipt ? 'Чеки не найдены' : 'Записей пока нет' }}
      />
    </Modal>
  )
}
